import {INTERNAL_PROJECTS, ProjectFeatureFlag} from 'common/report/constants';

import {NavReportConfig} from './types';

// make sure these values are in sync with common.styl's $chrome-header-height(s)
const chromeHeaderPrimaryHeight = 60;
const chromeHeaderSecondaryHeight = 50;
export const chromeHeaderHeight = chromeHeaderPrimaryHeight + chromeHeaderSecondaryHeight;

export enum HelpLabels {
  AskQuestions = `Ask Questions`,
  CommunityWidget = `Community FAQs`,
  ContactSupport = `Contact Support`,
  Intro = `Learn Mixpanel Basics`,
  LaunchTour = `Launch Tour`,
  LiveChat = `Live Chat`,
  Suggestion = `Make a Suggestion`,
  ZendeskHelp = `Search Help Center`,
}

export enum HelpTrackingEvent {
  CommunityWidget = `[Help Widget] Community Widget`,
  ExternalLink = `[Help Widget] External Link`,
  Intro = `[Help Widget] Learn Mixpanel Basics`,
  LaunchTour = `[Help Widget] Launch Tour`,
  LiveChat = `[Help Widget] Live Chat Started`,
  Suggestion = `[Help Widget] Suggestion`,
  Support = `[Help Widget] Support`,
  ZendeskHelp = `[Help Widget] Search Help Docs`,
}

export enum NavCategoryType {
  Analysis = `Analysis`,
  Dashboards = `Dashboards`,
  DataManagement = `Data Management`,
  MessagesAndExperiments = `Messages & Experiments`,
  Users = `Users`,
}

export enum Report {
  ABTesting = `ab_test`,
  Campaign = `campaign`,
  Cohorts = `cohorts`,
  Dashboards = `dashboards`,
  DataAudit = `audit`,
  Experiments = `experiment_reporting`,
  Explore = `explore`,
  Flows = `flows`,
  Funnels = `funnels`,
  Impact = `launch_analysis`,
  Insights = `insights`,
  Integrations = `integrations`,
  Lexicon = `lexicon`,
  LiveView = `live`,
  Journeys = `visual_journeys`,
  Messages = `messages`,
  Retention = `retention`,
  Signal = `signal`,
}

export const REPORT_INFO: Obj<NavReportConfig> = {
  [Report.Insights]: {
    description: `Deeply analyze your users and their actions`,
    endpoint: () => `insights`,
    icon: `brand-insights`,
    label: `Insights`,
    category: () => NavCategoryType.Analysis,
  },
  [Report.Flows]: {
    description: `Explore user paths in your product`,
    icon: `brand-flows`,
    label: `Flows`,
    endpoint: () => `flows`,
    category: () => NavCategoryType.Analysis,
  },
  [Report.Funnels]: {
    description: `Pinpoint where and why users drop off`,
    icon: `brand-funnels`,
    label: `Funnels`,
    endpoint: () => `funnels`,
    category: () => NavCategoryType.Analysis,
  },
  [Report.Retention]: {
    description: `Identify if users come back over time`,
    icon: `brand-retention`,
    label: `Retention`,
    endpoint: () => `retention`,
    category: () => NavCategoryType.Analysis,
  },
  [Report.Signal]: {
    description: `Identify actions that drive users to convert/retain`,
    icon: `brand-signal`,
    label: `Signal`,
    endpoint: () => `signal`,
    hidden: (reportContext): boolean => !reportContext.isProjectOrGlobalWorkspace,
    category: () => NavCategoryType.Analysis,
  },
  [Report.Impact]: {
    description: `Measure the impact of new features and campaigns`,
    icon: `brand-impact`,
    label: `Impact`,
    endpoint: () => `impact`,
    category: () => NavCategoryType.Analysis,
  },
  [Report.Experiments]: {
    beta: true,
    description: `Compare the impact of two sampled variants`,
    icon: `brand-experiments`,
    label: `Experiments`,
    endpoint: () => `experiment-reporting`,
    category: () => NavCategoryType.Analysis,
  },
  [Report.Explore]: {
    description: `Understand each user`,
    icon: `brand-explore`,
    label: `Explore`,
    endpoint: () => `explore`,
    endpointAliases: [`profile`],
    category: () => NavCategoryType.Users,
  },
  [Report.Cohorts]: {
    description: `Create groups of users to analyze across our reports`,
    icon: `brand-cohorts`,
    label: `Cohorts`,
    endpoint: (reportContext): string =>
      reportContext.features.hasProjectFeatureFlag(ProjectFeatureFlag.DisableEntityManagementCohorts)
        ? `cohorts`
        : `entity-management#~(entityType~'cohorts)`,
    category: (reportContext): NavCategoryType =>
      INTERNAL_PROJECTS.has(reportContext.projectId) ? NavCategoryType.DataManagement : NavCategoryType.Users,
  },
  [Report.Campaign]: {
    description: `Create and send a series of targeted messages`,
    endpoint: () => `triggers/campaign`,
    icon: `feature-tour`,
    label: `Campaign`,
    // stop showing "campaign" tab to new projects unless they have explicitly asked for it
    hidden: (reportContext): boolean =>
      (reportContext.projectId > 1_984_369 &&
        !reportContext.features.hasProjectFeatureFlag(ProjectFeatureFlag.NotifsCampaigns)) ||
      !reportContext.isProjectOrGlobalWorkspace,
    category: () => NavCategoryType.MessagesAndExperiments,
  },
  [Report.Messages]: {
    description: `Re-engage customers with targeted messages`,
    endpoint: () => `triggers/messages`,
    icon: `brand-messages`,
    label: `Messages`,
    category: () => NavCategoryType.MessagesAndExperiments,
  },
  [Report.Journeys]: {
    beta: true,
    description: `Plan and execute user journeys`,
    endpoint: () => `journeys`,
    icon: `brand-journeys`,
    label: `Journeys`,
    hidden: (reportContext): boolean =>
      !reportContext.features.hasProjectFeatureFlag(ProjectFeatureFlag.NotifsVisualJourneys),
    category: () => NavCategoryType.MessagesAndExperiments,
  },
  [Report.ABTesting]: {
    description: `Experiment to find the optimal user experiences`,
    endpoint: () => `ab_test`,
    icon: `brand-abtest`,
    label: `A/B Testing`,
    category: () => NavCategoryType.MessagesAndExperiments,
  },
  [Report.Lexicon]: {
    description: `Document and transform your events and properties`,
    icon: `brand-lexicon`,
    label: `Lexicon`,
    endpoint: () => `lexicon#transformations`,
    category: () => NavCategoryType.DataManagement,
  },
  [Report.DataAudit]: {
    description: `View possible issues with your implementation`,
    icon: `brand-audit`,
    label: `Data Audit`,
    beta: true,
    endpoint: () => `lexicon#audit`,
    hidden: (reportContext): boolean =>
      !reportContext.isProjectOrGlobalWorkspace ||
      reportContext.features.hasProjectFeatureFlag(ProjectFeatureFlag.HideReportDataAudit),
    category: () => NavCategoryType.DataManagement,
  },
  [Report.LiveView]: {
    description: `View a live streaming feed of events`,
    endpoint: () => `live`,
    icon: `brand-liveview`,
    label: `Live View`,
    category: () => NavCategoryType.DataManagement,
  },
  [Report.Integrations]: {
    description: `Empower your team to act on insights with the tools they already love`,
    icon: `brand-integrations`,
    label: `Integrations`,
    endpoint: () => `integrations`,
    hidden: (reportContext): boolean => !reportContext.isProjectOrGlobalWorkspace,
    category: () => NavCategoryType.DataManagement,
  },
};
